import { useCallback, useState } from 'react'

export type SortDirection = 'asc' | 'desc'

/** Sortierzustand für Tabellenlisten (Spalte + Richtung). */
export function useSortState<K extends string>(initialKey: K | null = null, initialDir: SortDirection = 'asc') {
  const [sortKey, setSortKey] = useState<K | null>(initialKey)
  const [direction, setDirection] = useState<SortDirection>(initialDir)

  const setSort = useCallback((key: K) => {
    setSortKey((prev) => {
      if (prev === key) {
        setDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
        return prev
      }
      setDirection('asc')
      return key
    })
  }, [])

  return { sortKey, direction, setSort }
}

/** Standardvergleich über eine Objekteigenschaft (Zahl oder String). */
export function compareByProp<T>(a: T, b: T, key: keyof T): number {
  const va = a[key]
  const vb = b[key]
  if (typeof va === 'number' && typeof vb === 'number') return va - vb
  return String(va ?? '').localeCompare(String(vb ?? ''), 'de', { sensitivity: 'base' })
}

/** Sortiert eine Kopie von `rows` gemäß Spalte und Richtung. */
export function sortRows<T, K extends keyof T>(
  rows: readonly T[],
  sortKey: K | null,
  direction: SortDirection,
  compare: (a: T, b: T, key: K) => number = compareByProp as (a: T, b: T, key: K) => number
): T[] {
  if (sortKey == null) return [...rows]
  const key = sortKey
  return [...rows].sort((a, b) => {
    const c = compare(a, b, key)
    return direction === 'asc' ? c : -c
  })
}
